import React, { useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import useSiteState from '@app/state/SiteState';
import useAppForm from '@app/site/_form';
import * as z from 'zod/v4';
import FormButton from '@app/component/form/button/FormButton';
import { useMutation } from '@tanstack/react-query';
import ProfileService from '@app/model/profile/service/ProfileService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import { shallow } from 'zustand/shallow';
import { SlackService } from '@app/model/slack/SlackService';

const ParityUserWelcomeFlowPage = React.memo(() => {

	const { principal } = useSiteState();
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

	const sendToSlack = (formValues: { hotel: string, name: string, email: string, phone: string, services: string[] }) => {
		return SlackService.send(SlackService.CHANNEL_NEW_COMPANY_FOR_PARITY, `Profile with id: ${principal!.id} and name: ${principal!.name} requests to join Parity module. Login with the username ${principal!.username} and visit /registration/create-parity-company-hotel\n\nContact Details:\n- Hotel: ${formValues.hotel}\n- Name: ${formValues.name}\n- Email: ${formValues.email}\n- Phone: ${formValues.phone}\n- Interested Services: ${formValues.services.join(', ')}`)
	};

	const ContactFormValidation = z.object({
		hotel: z.string().min(1, 'Hotel name is required.'),
		name: z.string().min(1, 'Name is required.'),
		email: z.email('Invalid email address.'),
		phone: z.string().min(1, 'Phone is required.'),
		services: z.array(z.string()).min(1, 'At least one service is required.'),
	});

	const form = useAppForm({
		defaultValues: {
			hotel: '',
			name: '',
			email: '',
			phone: '',
			services: [] as string[],
		},
		validators: {
			onChange: ContactFormValidation,
		},
		onSubmit: async ({ value }) => {
			console.log('Form submitted with values:', value);
			setIsSubmitted(true);
			return sendToSlack(value);
		},
	});

	return <form.AppForm>

		<div className="min-h-full bg-white">
			<div className="flex lg:h-dvh content-center justify-center flex-1">
				<div className="flex flex-1 flex-col justify-center px-6 py-12">
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}>
						<div className="mx-auto w-full max-w-sm lg:w-96">
							<div>
								<Link to="https://www.roompulse.io">
									<img
										className="h-10 w-auto"
										src="/assets/img/brand/logo-icon.png"
										alt="RoomPulse"
									/>
								</Link>
								<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
									Welcome to RoomPulse
								</h2>
								<p className="mt-2 text-sm leading-6 text-gray-500">
									You are about to <strong>activate</strong> your account! Please, fill the form below and our support
									team will be shortly in touch.
								</p>
							</div>

							<div className="mt-10">
								<div className="space-y-6">
									<div>
										<form.AppField name="hotel">
											{(field) => <field.TextField label="Hotel" placeholder="Insert hotel name" />}
										</form.AppField>
									</div>
									<div>
										<form.AppField name="name">
											{(field) => <field.TextField label="Name" placeholder="Insert name" />}
										</form.AppField>
									</div>
									<div>
										<form.AppField name="email">
											{(field) => <field.TextField label="Email" placeholder="Insert email" />}
										</form.AppField>
									</div>
									<div>
										<form.AppField name="phone">
											{(field) => <field.TextField label="Phone" placeholder="Insert phone number" />}
										</form.AppField>
									</div>

									<div>
										<div className="flex justify-between">
											<label htmlFor="input-normal" className="label">Select the service(s) you are interested
												in</label>
										</div>
										<div className="mt-1">
											<form.AppField name="services">
												{(field) => {
													return <div>
														<div className="flex items-center gap-x-4">
															<field.CheckboxField label="Rate Shopper" value="RateShopper"
																									 extraLabelClassName="text-gray-600" />
															<field.CheckboxField label="Parity" value="Parity" extraLabelClassName="text-gray-600" />
														</div>
														{field.state.meta.errors.length > 0 && (
															<p className="mt-2 text-sm text-red-600">
																{field.state.meta.errors[0]?.message}
															</p>
														)}
													</div>;
												}}
											</form.AppField>
										</div>
									</div>

									{!isSubmitted && <div>
										<form.SubmitButton state="primary" extraClassName="w-full">Submit</form.SubmitButton>
									</div>}
								</div>

								{isSubmitted && <div className="rounded-md bg-green-50 p-4 mt-2">
									<div className="flex">
										<div className="flex-shrink-0">
											<CheckCircleIcon className="h-5 w-5 text-green-400" />
										</div>
										<div className="ml-3">
											<div className="text-sm text-green-700">
												<p>Thank you for your interest. We will contact you within the next 1-2 business days. No
													further action is required from you in the meantime.</p>
											</div>
										</div>
									</div>
								</div>}
							</div>
						</div>
					</form>
				</div>
				<div className="relative hidden flex-1 lg:block">
					<img
						className="absolute inset-0 h-full w-full object-cover object-left"
						src="https://images.unsplash.com/photo-1722409194949-6d15fe881152?q=80&w=1548&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
						alt=""
					/>
				</div>
			</div>
			<RevHeroForm />
		</div>
	</form.AppForm>;
});

const RevHeroForm = React.memo(() => {

	const setPrincipal = useSiteState(state => state.setPrincipal, shallow);

	const [isShown, setIsShown] = React.useState<boolean>(true);
	const [isActivated, setIsActivated] = React.useState<boolean>(false);

	const toaster = useToaster();
	const { t } = useTranslation();

	const changeToRevHeroUserMutation = useMutation({
		mutationFn: ProfileService.changeParityUserToRevHeroUser,
		onSuccess: (data) => {
			setPrincipal(data);
		},
		onError: (error: string) => toaster.error('RevHero User', t(error)),
	});

	const form = useAppForm({
		defaultValues: {
			code: '',
		},
		validators: {
			onChange: z.object({
				code: z.string().min(1, 'Code is required.'),
			}),
		},
		onSubmit: async ({ value }) => changeToRevHeroUserMutation.mutateAsync(value.code).catch(() => {
		}),
	});

	return isShown && <div className="pointer-events-none fixed bottom-0 right-0 px-6 py-6">
		<div className="relative pointer-events-auto max-w-lg rounded-xl bg-white p-6 shadow-lg ring-1 ring-gray-900/10">
			{/* Header */}
			<header className="text-base leading-6 text-gray-900">
				<strong className="font-semibold">Are you a RevHero customer?</strong>
			</header>
			{/* Body */}
			<p className="text-sm text-gray-700">
				Click below to insert your code & activate your account.
			</p>

			{isActivated && <form.AppForm>
				<form className="mt-3 flex items-end gap-x-2" onSubmit={
					(e) => {
						e.preventDefault();
						e.stopPropagation();
					}
				}>
					<div className="w-full sm:max-w-xs">
						<form.AppField name="code">
							{(field) => <field.TextField label="Set your code" placeholder="Insert your code" />}
						</form.AppField>
					</div>
					<form.SubmitButton state="primary">Submit</form.SubmitButton>
				</form>
			</form.AppForm>}

			{!isActivated && <div className="mt-3 flex items-center gap-x-3">
				<FormButton state="primary" onClick={() => setIsActivated(true)}>
					Activate
				</FormButton>
			</div>}
		</div>
	</div>;
});

export default ParityUserWelcomeFlowPage;